import { useMemo } from 'react';
import { useIntl } from '@leagueplatform/locales';
import type {
  CustomWorkflowActionMenuItem,
  CustomWorkflowMultiStepActionMenuItem,
  ActionMenuItem,
} from '@web-config-app/core';
import { useGetTranslatedText } from '@web-config-app/core-react';
import { isUsingConfigAppBackend } from '@web-config-app/common';
import type { EntityWorkflowActionsProps } from '../../types/hooks';

export const useMenuItems = ({
  operations,
  status,
  domainEntity: entity,
  isEntitySaved,
  isReadOnly,
  entityConfigName,
  entityListPath,
  environmentName,
  resetEntityData,
  setIsDeepCloneModalOpen,
  entityHasIncludedEntities,
}: EntityWorkflowActionsProps & {
  setIsDeepCloneModalOpen: (isOpen: boolean) => void;
  entityHasIncludedEntities: boolean;
}): ActionMenuItem[] => {
  const { formatMessage } = useIntl();
  const getTranslatedText = useGetTranslatedText();
  const isUsingConfigAppBE = isUsingConfigAppBackend({
    entityLevelOverride: entity.isUsingConfigAppBackend,
  });

  const customActionMenuItems: Array<
    CustomWorkflowActionMenuItem | CustomWorkflowMultiStepActionMenuItem
  > = useMemo(
    () =>
      Object.entries(entity.customOperations ?? {}).map(
        ([actionId, operation]) => ({
          type: 'custom',
          id: actionId,
          operation,
          label:
            operation.label.text ?? formatMessage({ id: operation.label.key }),
          display: !isReadOnly,
          modal: operation.confirmationModal
            ? {
                type: 'confirmation',
                title: getTranslatedText(
                  entityConfigName,
                  operation?.confirmationModal.title,
                )!,
                description: getTranslatedText(
                  entityConfigName,
                  operation.confirmationModal.message,
                )!,
                confirmCtaText: getTranslatedText(
                  entityConfigName,
                  operation.confirmationModal.action.buttonLabel,
                )!,
                destructive: operation.confirmationModal.action.destructive,
              }
            : undefined,
        }),
      ),
    [
      entity.customOperations,
      formatMessage,
      isReadOnly,
      getTranslatedText,
      entityConfigName,
    ],
  );

  const isDeepCloneAvailable = isUsingConfigAppBE && entityHasIncludedEntities;

  const menuItems: ActionMenuItem[] = useMemo(() => {
    const cloneItem: ActionMenuItem = {
      type: 'clone',
      label: formatMessage({ id: 'DUPLICATE_AS_DRAFT' }),
      tooltip: formatMessage(
        { id: 'SAVE_BEFORE_YOU_DUPLICATE' },
        { entity: entityConfigName },
      ),
      // only allow the user to action on the clone menu item when the entity is saved
      disabled: !isEntitySaved,
      display: Boolean(operations?.clone?.enableAction) && !isReadOnly,
      // This action handles shallow and deepClone duplication. It will always have either an action or modal. If shallow clone, the action without modal will exist - if deepClone is available, the modal will open
      action:
        isEntitySaved && !isDeepCloneAvailable
          ? operations?.clone?.mutate
          : null,
      modal: isDeepCloneAvailable
        ? {
            type: 'option',
            title: formatMessage({
              id: 'DUPLICATE_THIS_AND_ALL_CHILDREN',
            }),
            description: formatMessage(
              { id: 'DUPLICATE_DISCLAIMER_MESSAGE' },
              { entity: entityConfigName },
            ),
            primaryCtaText: formatMessage({
              id: 'DUPLICATE_PRIMARY_ACTION_TEXT',
            }),
            primaryCtaAction: () => setIsDeepCloneModalOpen(true),
            secondaryCtaText: formatMessage(
              { id: 'DUPLICATE_SECONDARY_ACTION_TEXT' },
              { entity: entityConfigName },
            ),
            secondaryCtaAction: operations?.clone?.mutate,
          }
        : undefined,
    };

    switch (status) {
      case 'draft':
        return [
          cloneItem,
          {
            type: 'archive',
            label: formatMessage({ id: 'ARCHIVE' }),
            tooltip: formatMessage(
              { id: 'UNPUBLISH_BEFORE_YOU_ARCHIVE' },
              { entity: entityConfigName },
            ),
            disabled: false,
            display: Boolean(operations?.archive?.enableAction) && !isReadOnly,
            action: operations?.archive?.mutate,
          },
          {
            type: 'validate',
            label: formatMessage({ id: 'VALIDATE' }),
            disabled: false,
            display: Boolean(operations?.validate?.enableAction) && !isReadOnly,
            action: operations?.validate?.mutate,
          },
          ...customActionMenuItems,
        ];
      case 'published':
        return [
          {
            type: 'unpublish',
            label: formatMessage({ id: 'UNPUBLISH' }),
            disabled: false,
            display:
              Boolean(operations?.unpublish?.enableAction) && !isReadOnly,
            modal: {
              type: 'confirmation',
              confirmCtaAction: operations?.unpublish?.mutate,
              title: formatMessage(
                { id: 'UNPUBLISH_MODAL_TITLE' },
                { entity: entityConfigName, env: environmentName },
              ),
              description: formatMessage(
                {
                  id: 'UNPUBLISH_MODAL_DESCRIPTION',
                },
                { entity: entityConfigName },
              ),
              confirmCtaText: formatMessage(
                {
                  id: 'UNPUBLISH_PRIMARY_CTA',
                },
                { env: environmentName },
              ),
            },
          },
          cloneItem,
          {
            type: 'validate',
            label: formatMessage({ id: 'VALIDATE' }),
            disabled: false,
            display: Boolean(operations?.validate?.enableAction) && !isReadOnly,
            action: operations?.validate?.mutate,
          },
          ...customActionMenuItems,
        ];
      case 'archived':
        return [cloneItem];
      case 'new':
      default:
        return [
          {
            type: 'exitAndDiscardChanges',
            label: formatMessage({ id: 'EXIT_AND_DISCARD_CHANGES' }),
            disabled: false,
            display: !isEntitySaved,
            modal: {
              destructive: true,
              type: 'confirmation',
              confirmCtaAction: entityListPath,
              confirmCtaOnclick: resetEntityData,
              title: formatMessage(
                { id: 'EXIT_ENTITY_DETAIL_TITLE' },
                { entity: entityConfigName },
              ),
              description: formatMessage({
                id: 'EXIT_ENTITY_DETAIL_DESCRIPTION',
              }),
              confirmCtaText: formatMessage({
                id: 'EXIT_ENTITY_DETAIL_PRIMARY_CTA',
              }),
            },
          },
        ];
    }
  }, [
    status,
    isReadOnly,
    formatMessage,
    entityConfigName,
    isEntitySaved,
    operations?.clone?.enableAction,
    operations?.clone?.mutate,
    operations?.archive?.enableAction,
    operations?.archive?.mutate,
    operations?.unpublish?.enableAction,
    operations?.unpublish?.mutate,
    operations?.validate?.mutate,
    operations?.validate?.enableAction,
    isDeepCloneAvailable,
    customActionMenuItems,
    environmentName,
    entityListPath,
    resetEntityData,
    setIsDeepCloneModalOpen,
  ]);

  return useMemo(() => menuItems.filter((item) => item.display), [menuItems]);
};
